<template>
	<div class="qrcode-section-container">
		<div class="rooms-section">
			<div class="rooms-section-row">
				<div class="rooms-filter-container">
					<div class="room" :class="{active: activeRoom && (activeRoom.room_id === room.room_id)}" v-for="room in selectedRooms" :key="room.room_id" @click="toggleRoom(room)">{{ room.name ? room.name : getString('no_name') }}</div>
				</div>
			</div>
			<div v-if="selectedRooms && selectedRooms?.length" class="order-switch" @click="activeRoomSettings = true">
				<img :src="'https://img.qromo.io/img/icons/icon-settings-primary'+(userStore.darkMode ? '-dark' : '')+'.svg'" class="icon-dimension-15"/>
			</div>
			<div class="order-switch" @click="newRoomActive = true">
				<img :src="'https://img.qromo.io/img/vue/icons/icon-plus'+(userStore.darkMode ? '-dark' : '')+'.svg'" class="icon-dimension-15" />
			</div>
		</div>
		<div class="card-section card-margin">
			<AppCard v-if="activeRoom" no-header :title="activeRoom.name">
				<template v-slot:body>
					<div class="qr-management-container">
						<div class="qr-management" v-if="!roomsStore.editMode">
							<div class="left-side">
								<p class="qr-title">{{ getString('qr_management') }}</p>
								<p class="qr-subtitle" v-if="activeRoom.type">{{ activeRoom.type === "per_table" ? getString('qr_assigned') : getString('qr_unique') }}</p>
								<p class="qr-subtitle" v-if="activeRoom.type && activeRoom.type === 'table_number'">{{ getString('table_number') }}</p>
								<p class="qr-subtitle" v-if="activeRoom.type && activeRoom.type === 'customer_name'">{{ getString('customer_name') }}</p>
							</div>
							<div class="right-side">
								<div class="buttons-container">
									<AppButton outline :text="activeRoom.type ? getString('download') : getString('generate_qr')" @click="activeRoom.type ? downloadQrsTables() : generateQr()"  :loading="loadingDownload" />
									<AppButton v-if="activeRoom.type" outline :text="getString('print_with_us')" @click="activePrintQr = true" />
								</div>
							</div>
						</div>
						<div class="tables-container">
							<div class="table-container-wrapper" v-for="table in activeRoomTables" :key="table.room_table_id">
								<div class="table-container">
									<div class="table-name-container" @click="editTableName(table)">
										<input class="table-name-input" v-if="table.editName" :ref="'edit-table-'+table.room_table_id" type="text" :value="table.table_name" @focusout="endEditTable(table)" :placeholder="getString('table_number')" />
										<div class="table-name" v-else>{{ table.table_name }}</div>
									</div>
									<div class="table-qr" :ref="'table-qr-'+table.room_table_id" v-show="!roomsStore.editMode"></div>
									<AppButton class="table-qr-button" outline :text="getString('edit')" @click="editTable(table)" v-if="roomsStore.editMode" />
									<div class="seat-container">
										<div class="seat">{{ roomsStore.editMode ? getString('position') : getString('people') }}</div>
										<TouchNumberInput :value="roomsStore.editMode ? table.order_number : table.seat_number" @changed-value="v => changedTableValue(v, table)" />
									</div>
								</div>
							</div>
						</div>
						<div class="tables-pagination-row">
							<PaginationRow class="mt-20" :page="tablesPage" :page-size="tablesPageSize" :total="activeRoom.tables.length" :initial-page-size="25" @page-changed="pageChanged"/>
						</div>
					</div>
				</template>
			</AppCard>
			<div class="no-room" v-else>
				<div class="no-room-text">{{ getString('no_room') }}</div>
				<div class="no-room-subtitle">{{ getString('no_room_explain') }}</div>
			</div>
		</div>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="newRoomActive" @toggling-popup="toggleNewRoom">
			<div class="new-room-container" v-if="newRoomActive">
				<NewRoomForm @close="newRoomActive = false" @created="v => {newRoomActive = false;roomsRefresh(v);}"></NewRoomForm>
			</div>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="generateQrActive" @toggling-popup="toggleGenerateQr">
			<div class="generate-container" v-if="generateQrActive">
				<GenerateQrForm :room-id="activeRoom ? activeRoom.room_id : null" @chosen="chosenMode"></GenerateQrForm>
			</div>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidthLarge" :active="activePrintQr" @toggling-popup="togglePrintQr">
			<div class="print-qr-container" v-if="activePrintQr">
				<PrintQrCodes />
			</div>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="activeRoomSettings" @toggling-popup="toggleRoomSettings">
			<div class="room-settings-container" v-if="activeRoom && activeRoomSettings">
				<RoomSettings :room-id="activeRoom.room_id" @close="v => {activeRoomSettings = false;roomsRefresh(v);}" />
			</div>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="!!editingTableId" @toggling-popup="toggleTableEdit">
			<div class="edit-table-container" v-if="editingTableId">
				<TableEdit :room-table-id="editingTableId" @deleted="deletedTable" />
			</div>
		</AppBottomSheet>
		<AppBottomSheet :force-width="bottomSheetWidth" :active="linkBottomSheetActive" @toggling-popup="toggleLinkBottomSheet">
			<div class="link-bottom-sheet-container" v-if="linkBottomSheetActive">
				<CardSlashedMenuLink :force-show="true" @link-saved="onLinkSaved" />
			</div>
		</AppBottomSheet>
		<div id="fake-qr-element" style="display: none;"></div>
	</div>
</template>

<script>
import {useLanguageStore, useNotificationStore, useScriptStore, useSvgStore} from "@/shared/stores";
import {useFunnelStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import AppCard from "@/shared/components/AppCard.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import NewRoomForm from "@/console-new/components/config/qrcode/NewRoomForm.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import AppPaginationButtonsGroup from "@/shared/components/AppPaginationButtonsGroup.vue";
import PaginationRow from "@/console-new/components/shared/PaginationRow.vue";
import GenerateQrForm from "@/console-new/components/config/qrcode/GenerateQrForm.vue";
import PrintQrCodes from "@/console-new/components/config/qrcode/PrintQrCodes.vue";
import RoomSettings from "@/console-new/components/config/qrcode/RoomSettings.vue";
import TableEdit from "@/console-new/components/config/qrcode/TableEdit.vue";
import CardSlashedMenuLink from "@/console-new/components/shared/CardSlashedMenuLink.vue";
import API from "@/shared/helpers/API";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		const scriptStore = useScriptStore();
		const funnelStore = useFunnelStore();
		return {getString: languageStore.getString, funnelStore, showNotification: notificationStore.showNotification, userStore, roomsStore, getIcon: svgStore.get, scriptStore}
	},
	name: "QrCodeContainer",
	inject: ['providedAction'],
	components: {
		PrintQrCodes,
		GenerateQrForm,
		PaginationRow,
		RoomSettings,
		TableEdit,
		CardSlashedMenuLink,
		AppPaginationButtonsGroup, AppButtonIcon, AvailabilityDot, AppButton, AppBottomSheet, AppCard, NewRoomForm, TouchNumberInput},
	mixins: [WindowWidth],
	data() {
		return {
			savingTables: false,
			newRoomActive: false,
			tablesPageSize: 25,
			tablesPage: 0,
			loadedQrLibrary: false,
			loadingDownload: false,
			generateQrActive: false,
			activePrintQr: false,
			activeRoomSettings: false,
			editingTableId: null,
			linkBottomSheetActive: false,
			pendingGenerateQr: false,
		}
	},
	computed: {
		selectedRooms() {
			return this.roomsStore.roomsTables ? this.roomsStore.roomsTables : [];
		},
		activeRoom() {
			return this.roomsStore.activeRoomId ? this.selectedRooms.find(room => room.room_id === this.roomsStore.activeRoomId) : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		bottomSheetWidthLarge() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		activeRoomTables() {
			if(this.activeRoom) {
				return this.activeRoom.tables.sort((a, b) => a.order_number - b.order_number).slice(this.tablesPage * this.tablesPageSize, (this.tablesPage + 1) * this.tablesPageSize);
			} else {
				return [];
			}
		},
	},
	methods: {
		changedTableValue(value, table) {
			if(this.roomsStore.editMode) {
				table.order_number = value;
			} else {
				table.seat_number = value;
			}
		},
		pageChanged(page, pageSize) {
			this.tablesPage = page;
			this.tablesPageSize = pageSize;

			this.$nextTick(() => {
				this.generateQrImages();
			});

		},
		toggleRoom(room) {
			this.roomsStore.setActiveRoom(room.room_id);
			this.tablesPage = 0;
		},
		toggleNewRoom(isOpen) {
			if(!isOpen) {
				this.newRoomActive = false;
			}
		},
		toggleGenerateQr(isOpen) {
			if(!isOpen) {
				this.generateQrActive = false;
			}
		},
		togglePrintQr(isOpen) {
			if(!isOpen) {
				this.activePrintQr = false;
			}
		},
		toggleRoomSettings(isOpen) {
			if(!isOpen) {
				this.activeRoomSettings = false;
			}
		},
		toggleTableEdit(isOpen) {
			if(!isOpen) {
				this.editingTableId = null;
			}
		},
		deletedTable() {
			if(this.editingTableId) {
				this.editingTableId = null;
				if(this.activeRoom) {
					this.roomsStore.getBusinessRooms(null, true);
				}
			}
		},
		roomsRefresh(roomId) {
			this.roomsStore.getBusinessRooms(() => {
				if(this.roomsStore.rooms.length > 0) {
					let room = roomId ? this.roomsStore.rooms.find(r => r.room_id === roomId) : null;
					if(room) {
						this.roomsStore.setActiveRoom(room.room_id);
					}
				}
			}, true);
		},
		endEditTable(table) {
			table.editName = false;
			table.table_name = this.$refs['edit-table-' + table.room_table_id][0].value;
		},
		editTableName(table) {
			table.editName = true;
			this.$nextTick(() => {
				if(this.$refs['edit-table-' + table.room_table_id]) {
					this.$refs['edit-table-' + table.room_table_id][0].focus();
				}
			});
		},
		editTable(table) {
			this.editingTableId = table.room_table_id;
		},
		chosenMode(type) {
			this.generateQrActive = false;
			if(this.activeRoom) {
				this.activeRoom.type = type;
				this.generateQrImages();
			}
		},
		generateQrImages() {
			if(this.loadedQrLibrary) {

				if(this.activeRoom && this.activeRoom.type) {

					this.activeRoomTables.forEach((table) => {
						if(table.qrCode) {
							table.qrCode.clear();
							table.qrCode = null;
						}

						if(this.$refs['table-qr-' + table.room_table_id] && this.$refs['table-qr-' + table.room_table_id][0] && table.token) {
							this.$refs['table-qr-' + table.room_table_id][0].innerHTML = '';
							table.qrCode = new QRCode(this.$refs['table-qr-' + table.room_table_id][0], {
								text: URL_HTML+"/q/?t="+table.token,
								width: 65,
								height: 65
							});
						}

					});

				}

			}
		},
		generateQr() {
			// Check if business has a link
			if (!this.userStore.currBusiness || !this.userStore.currBusiness.link || this.userStore.currBusiness.link.length === 0) {
				// Show link bottom sheet first
				this.pendingGenerateQr = true;
				this.linkBottomSheetActive = true;
			} else {
				// Proceed with QR generation
				this.generateQrActive = true;
			}
		},
		toggleLinkBottomSheet(isOpen) {
			if(!isOpen) {
				this.linkBottomSheetActive = false;
				this.pendingGenerateQr = false;
			}
		},
		onLinkSaved() {
			// Close link bottom sheet
			this.linkBottomSheetActive = false;
			// If there was a pending QR generation, proceed with it
			if (this.pendingGenerateQr) {
				this.pendingGenerateQr = false;
				// Small delay to ensure userStore is updated
				this.$nextTick(() => {
					this.generateQrActive = true;
				});
			}
		},
		downloadQrsTables() {
			if(!this.activeRoom || this.activeRoom.tables.length === 0) {
				return;
			}
			// Check if business has a link
			if (!this.userStore.currBusiness || !this.userStore.currBusiness.link || this.userStore.currBusiness.link.length === 0) {
				// Show link bottom sheet first
				this.pendingGenerateQr = false; // Reset flag
				this.linkBottomSheetActive = true;
				return;
			}
			this.loadingDownload = true;
			this.scriptStore.loadScriptsParallel(["zip", "qrs"], () => {
				let zip = new JSZip();
				let qrsToRender = [];
				for(let y in this.activeRoom.tables) {
					let table = this.activeRoom.tables[y];
					if(table.token) {
						let qrNameAlfanum = table.room_table_id+"-"+(this.activeRoom?.name || '')+"-"+table.table_name;
						qrsToRender.push({
							name: this.sanitizeFilename(qrNameAlfanum),
							value: URL_HTML+"/q/?t="+table.token,
						});
					}
				}
				this.renderQr(qrsToRender, zip, 0);
			});
		},
		renderQr(qrsToRender, zip, currI) {
			let zipName = "qr-codes-"+this.sanitizeFilename(this.userStore.currBusiness.name);
			zipName += "-"+this.sanitizeFilename(this.activeRoom?.name || '');
			if(currI < qrsToRender.length) {
				let qrToRender = qrsToRender[currI];
				let qr = new QRCode("fake-qr-element", {
					text: qrToRender.value,
					width: 300,
					height: 300,
					onRenderingEnd: () => {
						let imgBase64 = qr._oDrawing._elImage.currentSrc;
						if (typeof imgBase64 === "undefined" || (typeof imgBase64 === "string" && imgBase64.length === 0)) {
							imgBase64 = qr._oDrawing._elImage.href;
						}
						if (typeof imgBase64 === "undefined" || (typeof imgBase64 === "string" && imgBase64.length === 0)) {
							imgBase64 = qr._oDrawing._elImage.src;
						}
						let index = imgBase64.indexOf(",");
						if (index !== -1) {
							imgBase64 = imgBase64.substring(index + 1, imgBase64.length);
						}
						zip.folder(zipName).file("#"+qrToRender.name+".png", imgBase64, {base64: true});
						qr.clear();
						this.renderQr(qrsToRender, zip, currI+1);
					}
				});
			} else {
				zip.generateAsync({type: "blob"}).then(content => {
					this.saveAs(content, this.sanitizeFilename(zipName)+".zip");
					this.loadingDownload = false;
				});
			}
		},
		saveAs(blob, filename) {
			if (typeof navigator.msSaveOrOpenBlob !== 'undefined') {
				return navigator.msSaveOrOpenBlob(blob, filename);
			} else if (typeof navigator.msSaveBlob !== 'undefined') {
				return navigator.msSaveBlob(blob, filename);
			} else {
				let elem = window.document.createElement('a');
				elem.href = window.URL.createObjectURL(blob);
				elem.download = filename;
				elem.setAttribute('style', 'display:none;');
				(document.body || document.documentElement).appendChild(elem);
				if (typeof elem.click === 'function') {
					elem.click();
				} else {
					elem.target = '_blank';
					elem.dispatchEvent(new MouseEvent('click', {
						view: window,
						bubbles: true,
						cancelable: true
					}));
				}
				URL.revokeObjectURL(elem.href);
			}
		},
		sanitizeFilename(filename) {
			return filename.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-/, '').replace(/-$/, '');
		},
		saveRoomTables() {
			if(!this.activeRoom || this.savingTables) {
				return;
			}
			let elements = [];
			this.activeRoomTables.forEach((table) => {
				elements.push({
					room_table_id: table.room_table_id,
					table_name: table.table_name,
					seat_number: table.seat_number,
					order_number: table.order_number,
				});
			});
			if(elements.length === 0) {
				this.savingTables = false;
				this.showNotification('generic-fail');
				return;
			}
			API.init().saveRoomTables(this.userStore.currBusinessId, this.activeRoom.room_id, JSON.stringify(elements), (result) => {
				if(result && result.result === "OK") {
					this.showNotification('generic-success');
					if(this.roomsStore.editMode) {
						this.roomsStore.editMode = false;
					}
				} else {
					this.showNotification('generic-fail');
				}
			});
		}
	},
	mounted() {
		this.scriptStore.loadScript("qrs", () => {
			this.loadedQrLibrary = true;
			this.$nextTick(() => {
				this.generateQrImages();
			});
		});
		this.roomsStore.getBusinessRooms();
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.roomsStore.getBusinessRooms();
			}
		},
		activeRoom(newVal, oldVal) {
			if(newVal && newVal !== oldVal) {
				this.$nextTick(() => {
					this.generateQrImages();
				});
			}
		},
		providedAction(newProvidedObject) {
			if(newProvidedObject.roomsToSave) {
				this.saveRoomTables();
				this.providedAction.resetRoomsToSave();
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/console-new/assets/scss/_variables.scss" as *;
.qrcode-section-container {

	.rooms-section {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		position: relative;
		padding: 10px;
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);
		border-radius: 0 0 13px 13px;
		background: rgba(var(--rgb-bg-primary-1), 67%);
		box-shadow: inset -3px -3px 6px var(--color-box-shadow-2);
		gap: 10px;


		.order-switch {
			border-radius: 13px;
			border: 1px solid var(--color-primary);
			display: flex;
			justify-content: center;
			align-items: center;
			padding: 9px;
			transition: background-color 0.25s ease-in-out;
			cursor: pointer;
			flex-shrink: 0;

			img {
				width: 16px;
				height: 16px;
			}

		}

		.rooms-section-row {
			display: flex;
			flex-direction: row;
			align-items: center;
			overflow-x: auto;
			-ms-overflow-style: none;
			scrollbar-width: none;
			border-radius: 13px;
			background-color: var(--color-bg-primary-2);
			box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
			min-height: 51px;
			flex-grow: 1;

			.rooms-filter-container {
				border-radius: 13px;
				display: flex;
				flex-direction: row;
				justify-content: flex-start;
				align-items: center;
				padding: 8px;
				gap: 6px;
				width: 100%;
				height: 66px;

				.room {
					border-radius: 13px;
					border: 1px solid var(--color-primary);
					padding: 7px 12px;
					@include truncateString(1);
					font-size: 15px;
					color: var(--color-primary);
					width: fit-content;
					cursor: pointer;
					user-select: none;
					transition: background-color 0.3s ease;
					display: flex;
					align-items: center;
					justify-content: center;
					height: 50px;
					flex: 1;
					min-width: 140px;
					max-width: 250px;

					@media (max-width: 950px) {
						min-width: 120px;
					}

					@media (max-width: 850px) {
						min-width: 100px;
					}

					@media (max-width: 750px) {
						min-width: 120px;
					}

					@media (max-width: 550px) {
						min-width: 100px;
					}

					&.active {
						background-color: var(--color-active-button);
						color: var(--color-active-button-text);
						p {
							color: var(--color-active-button-text);
						}
					}
				}

				@media (max-width: 900px) {
					padding: 8px;
					gap: 8px;
					min-height: 46px;

					.room {
						padding: 7px 8px;
					}
				}

			}

		}
	}

	.no-room {
		width: 100%;
		background-color: var(--color-bg);
		border-radius: 13px;
		border: 2px dashed $primary;
		padding: 20px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		min-height: 130px;
		gap: 6px;

		.no-room-text {
			font-size: 18px;
			color: $primary;
			font-weight: 700;
		}

		.no-room-subtitle {
			font-size: 14px;
			color: $primary;
			font-weight: 400;
		}
	}

	.availability-container {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		gap: 8px;
	}

	.actions-container {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: 8px;
	}

	.qr-management-container {
		position: relative;

		.qr-management {
			display: flex;
			justify-content: space-between;
			align-items: stretch;
			gap: 10px;
			flex-wrap: wrap;

			.left-side {
				display: flex;
				justify-content: flex-start;
				align-items: flex-start;
				flex-direction: column;
				gap: 4px;

				.qr-title {
					font-size: 15px;
					font-weight: 700;
				}

				.qr-subtitle {
					font-size: 14px;
					font-weight: 400;
				}
			}

			.right-side {
				display: flex;
				justify-content: flex-end;
				align-items: flex-end;
				gap: 12px;
				margin-left: auto;

				.qr-code-container {
					width: 80px;
					height: 80px;
					background-color: var(--color-bg);

					&:deep(img) {
						width: 100%;
						height: 100%;
					}
				}

				.buttons-container {
					display: flex;
					justify-content: flex-end;
					align-items: flex-end;
					flex-direction: row;
					gap: 8px;

					@media (max-width: 600px) {
						flex-direction: column;

					}
				}
			}


			&:after {
				content: "";
				display: block;
				width: 100%;
				height: 1px;
				background-color: var(--color-border-2);
			}
		}

		.tables-container {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			flex-wrap: wrap;

			.table-container-wrapper {
				--num-items: 8;
				width: calc(calc(100% / var(--num-items)));
				display: flex;
				justify-content: center;
				align-items: flex-start;
				margin-top: 10px;

				@media (max-width: 1880px) {
					--num-items: 7;
				}

				@media (max-width: 1700px) {
					--num-items: 6;
				}

				@media (max-width: 1510px) {
					--num-items: 5;
				}

				@media (max-width: 1300px) {
					--num-items: 4;
				}

				@media (max-width: 1080px) {
					--num-items: 3;
				}

				@media (max-width: 920px) {
					--num-items: 4;
				}

				@media (max-width: 780px) {
					--num-items: 3;
				}

				@media (max-width: 560px) {
					--num-items: 2;
				}

				@media (max-width: 374px) {
					--num-items: 1;
				}

				.table-container {
					background-color: var(--color-bg-primary-1);
					border-radius: 13px;
					box-shadow: 0 3px 5px var(--color-bg-primary-2);
					display: flex;
					flex-direction: column;
					gap: 8px;
					max-width: 165px;
					flex-grow: 1;
					flex-shrink: 0;
					align-items: center;

					@media (max-width: 768px) {
						max-width: 155px;
					}

					.table-name-container {
						padding: 6px 8px;
						background-color: var(--color-bg-primary-2);
						border-radius: 7px;
						cursor: pointer;
						margin-top: 8px;
						margin-left: 6px;
						margin-right: 6px;
						width: calc(100% - 16px);

						.table-name {
							font-size: 16px;
							font-weight: 700;
							color: $primary;
							text-align: center;
							@include truncateString(1);
						}

						input {
							background-color: var(--color-bg-primary-2);
							font-size: 16px;
							font-weight: 700;
							color: $primary;
							width: 100%;
							height: 100%;
							border: none;
						}

					}

					.table-qr {
						border-radius: 6px;
						display: flex;
						justify-content: center;
						align-items: center;
						background-color: var(--color-txt-white);
						width: fit-content;
						:deep(img) {
							padding: 5px;
						}
					}

					.table-qr-button {
						margin: 0 auto;
					}

					.seat-container {
						width: 100%;
						display: flex;
						flex-direction: column;
						box-shadow: 0 -2px 2px var(--color-box-shadow-2);
						border-radius: 13px;
						background-color: var(--color-bg);

						.seat {
							font-size: 13px;
							font-weight: 400;
							color: var(--color-primary);
							text-align: center;
							margin-top: 4px;
						}

						:deep(.input-element) {
							box-shadow: unset;
						}
					}
				}
			}

			&:after {
				content: "";
				flex-grow: 1;
			}

		}

		.tables-pagination-row {
			//background-color: var(--color-bg-primary-2);
		}

	}
}

.card-margin {
	margin-top: $mainPadding;
}
.new-room-container {
	z-index: 9999;
}
</style>